import { useRouter } from 'next/router'
import { useEffect, useRef, useState } from 'react'
import { graphqlFetch, GraphqlFetchOutput } from '@pylot-data/graphqlFetch'
import type {
    AddProductsToCartOutput as AddProductsToCartOutputRaw,
    Cart,
    CartItemInput,
    ProductInterface
} from '@pylot-data/pylotschema'
import { addToCartMutation } from '@corratech/pylot-cart-manager/src/graphql/addToCartMutation'
import { CartData, useCart } from '@corratech/pylot-cart-manager/src/use-cart'
import { enqueueCartMutation } from '@corratech/pylot-cart-manager/src/utils/enqueueCartMutation'
import { getIsSignedIn } from '@corratech/pylot-cart-manager/src/utils/getIsSignedIn'
import { getOptimisticAddToCartData } from '@corratech/pylot-cart-manager/src/utils/getOptimisticAddToCartData'
import { useUI } from '@corratech/pylot-ui/context'
import { CartItemInterface, Maybe } from '@pylot-data/pylotschema'
import { publishAddToCartMse } from '@lib/utils/adobe-events-converters'
import {
    blockGiftCardMixIfNeeded,
    openGiftCardMixAlertIfBackendRejected
} from '@corratech/pylot-cart-manager/src/utils/giftCardCartMix'
import { consumeCartSnapshotAfterEmpty } from '@corratech/pylot-cart-manager/src/utils/waitUntilCartIsEmpty'
import { ensureCart } from '@corratech/pylot-cart-manager/src/create-cart'

export interface PersonalizationDataInput {
    uid: string
    value: string
}

export interface AddProductsToCartInput {
    cartId: string
    cartItems: CartItemInput[]
    isSignedIn: boolean
}

export interface AddProductsToCartOutput {
    addProductsToCart: AddProductsToCartOutputRaw
}

export interface UseAddToCartReturn {
    addToCart: (
        cartItems: CartItemInput[],
        products?: ProductInterface[],
        excludeItems?: string[],
        shouldDoOptimisticUpdate?: boolean,
        displayLoadingIndicator?: boolean
    ) => Promise<CartData | undefined>
    isAdding: boolean
}

const formatData = (
    output: GraphqlFetchOutput<AddProductsToCartOutput>,
    previousCart?: Maybe<Cart>
): ((oldData?: CartData) => CartData) | CartData => {
    const outputData = output.data?.addProductsToCart
    const errors = output.errors
    const cart = outputData?.cart as Cart

    // El fragmento liviano de addProductsToCart en el edge (EVO-225) no pide
    // shipping_addresses y lo devuelve en null. Sin esto el null pisa el
    // carro cacheado y el checkout revienta al leerlo (pantalla en blanco).
    if (cart && cart.shipping_addresses == null) {
        cart.shipping_addresses = previousCart?.shipping_addresses ?? []
    }

    return {
        data: { cart },
        errors: errors,
        user_errors: outputData?.user_errors
    }
}

// During 'editing cart item' we remove an old item and add a new item simultaneously
// So, we have to ignore the outdated cart item
const filterExcluded = (cart: Cart, excludeItems?: string[]): Cart => {
    if (cart.items && excludeItems) {
        cart.items = cart.items.filter(
            (item) => item && !excludeItems.includes(item.uid)
        )
    }
    return cart
}

export const useAddToCart = (): UseAddToCartReturn => {
    const { locale } = useRouter()
    const { data, mutate } = useCart()
    const cartDataRef = useRef(data)
    cartDataRef.current = data
    const { setMinicartLoading } = useUI()
    const [isAdding, setIsAdding] = useState(false)
    const [optimisticItems, setOptimisticItems] =
        useState<Maybe<Array<Maybe<CartItemInterface>>>>()

    // If cart data is being updated during ATC request - restore optimistic items
    useEffect(() => {
        if (isAdding && data?.data?.cart && optimisticItems) {
            data.data.cart.items = optimisticItems
            mutate(data, false)
        }
    }, [data])

    const addToCart = async (
            cartItems: CartItemInput[],
            products?: ProductInterface[],
            excludeItems?: string[],
            shouldDoOptimisticUpdate = true,
            displayLoadingIndicator?: boolean
        ) => {
            setIsAdding(true)

            if (displayLoadingIndicator) setMinicartLoading(true)

            const pendingMixAdd = {
                cartItems,
                products,
                excludeItems,
                shouldDoOptimisticUpdate,
                displayLoadingIndicator
            }
            const currentData =
                consumeCartSnapshotAfterEmpty() ?? cartDataRef.current
            const mixBlock = blockGiftCardMixIfNeeded(
                currentData?.data?.cart,
                products,
                pendingMixAdd
            )
            if (mixBlock) {
                setIsAdding(false)
                setMinicartLoading(false)
                return mixBlock
            }

            if (products && shouldDoOptimisticUpdate) {
                const optimisticData = getOptimisticAddToCartData(
                    currentData!,
                    cartItems,
                    products
                )
                optimisticData.data.cart = filterExcluded(
                    optimisticData.data.cart,
                    excludeItems
                )
                setOptimisticItems(optimisticData.data.cart.items)
                mutate(optimisticData, false)
            }

            // El carro de invitado se crea recién aquí, al agregar el primer
            // producto, y no al cargar la página (ver ensureCart).
            const cartId =
                currentData?.data?.cart?.id ||
                (await ensureCart({ locale })) ||
                ''

            const response = await graphqlFetch<
                AddProductsToCartInput,
                AddProductsToCartOutput
            >({
                query: addToCartMutation,
                variables: {
                    cartId: String(cartId),
                    cartItems,
                    isSignedIn: getIsSignedIn()
                },
                locale
            })

            const addedPayload = response?.data?.addProductsToCart
            const failureMessages = [
                ...(response?.errors ?? []).map((error) => error?.message),
                ...(addedPayload?.user_errors ?? []).map(
                    (error) => error?.message
                )
            ]
            openGiftCardMixAlertIfBackendRejected(
                pendingMixAdd,
                failureMessages
            )

            // if response contains errors
            if (response?.errors?.length || !addedPayload?.cart) {
                // Leave the old cart data untouched, but indicate, that error has occurred.
                // And trigger the revalidation to get latest cart data with no errors
                setIsAdding(false)
                setMinicartLoading(false)
                return mutate((oldData) => ({
                    data: oldData!.data,
                    errors: response.errors,
                    ...(addedPayload?.user_errors
                        ? { user_errors: addedPayload.user_errors }
                        : {})
                }))
            }

            if (addedPayload.cart.items) {
                addedPayload.cart = filterExcluded(
                    addedPayload.cart,
                    excludeItems
                )
            }

            setOptimisticItems(null)
            setMinicartLoading(false)

            console.log(
                '[LiveSearch][ATC][framework/use-add-to-cart] addProductsToCart OK, preparando MSE',
                {
                    cartId: addedPayload.cart?.id,
                    items: addedPayload.cart?.items?.length ?? 0
                }
            )
            if (typeof window !== 'undefined') {
                const mse = (window as any).magentoStorefrontEvents
                if (mse) {
                    publishAddToCartMse(mse, addedPayload.cart)
                } else {
                    console.warn(
                        '[LiveSearch][ATC][framework/use-add-to-cart] sin MSE en window tras éxito GraphQL'
                    )
                }
            } else {
                console.log(
                    '[LiveSearch][ATC][framework/use-add-to-cart] omitido: entorno sin window'
                )
            }

            if (shouldDoOptimisticUpdate) {
                const result = await mutate(
                    formatData(response, currentData?.data?.cart),
                    false
                )
                // If there were errors - revalidate cart data
                if (
                    result?.errors?.length ||
                    addedPayload?.user_errors?.length
                ) {
                    await mutate(undefined, true)
                }
                setIsAdding(false)
                return result
            }

            return {
                data: {
                    cart: addedPayload.cart,
                    isOptimistic: false
                }
            }
        }

    return {
        addToCart: (
            cartItems,
            products,
            excludeItems,
            shouldDoOptimisticUpdate,
            displayLoadingIndicator
        ) =>
            enqueueCartMutation(
                String(cartDataRef.current?.data?.cart?.id ?? ''),
                () =>
                    addToCart(
                        cartItems,
                        products,
                        excludeItems,
                        shouldDoOptimisticUpdate,
                        displayLoadingIndicator
                    )
            ),
        isAdding
    }
}
